/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { memo } from "react";
import {
  EuiListGroup,
  EuiListGroupItem,
  EuiPopover,
} from "@inscopix/ideas-eui";
import {
  parseResourceTierCellValue,
  RESOURCE_TIERS_BY_ID,
  ResourceTier,
} from "types/ResourceTiers";
import { isDefined } from "utils/isDefined";
import { ToolSpec } from "types/json-schemas/type_guards";
import { formatToolResources } from "../layout/DataTable";
import { CellValue } from "@inscopix/ideas-hyperformula";

const styles = {
  popover: css`
    & {
      height: 100%;
      width: 100%;
      display: flex;
      align-items: center;
      padding: 0px 11px;
    }
  `,
  panel: css`
    max-height: max(200px, 50vh);
    overflow-y: scroll;
  `,
};

interface CellEditorComputeResourcesBaseProps {
  toolVersionResources: ToolSpec["resources"];
  toolVersionCredits: number;
  value: CellValue;
  onSelectResourceTier: (tier: ResourceTier["id"] | null) => void;
  stopEditing: (cancel?: boolean) => void;
}

/** Cell editor for cells in the compute resources column of an analysis table */
export const CellEditorComputeResourcesBase = memo(
  function CellEditorComputeResourcesBase({
    toolVersionCredits,
    toolVersionResources,
    value,
    stopEditing,
    onSelectResourceTier,
  }: CellEditorComputeResourcesBaseProps) {
    const parsedValue = parseResourceTierCellValue(value);

    return (
      <EuiPopover
        anchorPosition="downCenter"
        button={
          <span>
            {parsedValue.isResourceTierId
              ? formatToolResources(
                  {
                    resources: toolVersionResources,
                    credits: toolVersionCredits,
                  },
                  parsedValue.value,
                )
              : parsedValue.value}
          </span>
        }
        isOpen
        panelPaddingSize="none"
        css={styles.popover}
        closePopover={stopEditing}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            e.preventDefault();
            stopEditing(true);
          }
        }}
        display="block"
        repositionOnScroll={false}
      >
        <EuiListGroup css={styles.panel} maxWidth="100%">
          {[
            <EuiListGroupItem
              key={"DEFAULT_TIER"}
              iconType={value === "" || value === null ? "check" : "empty"}
              label={formatToolResources(
                {
                  resources: toolVersionResources,
                  credits: toolVersionCredits,
                },
                null,
              )}
              onClick={() => {
                if (value !== null && value !== "") {
                  onSelectResourceTier(null);
                  stopEditing();
                }
              }}
            />,
            ...Object.values(RESOURCE_TIERS_BY_ID)
              .filter(isDefined)
              .map((tier) => (
                <EuiListGroupItem
                  id={tier.id.toString()}
                  key={tier.id}
                  iconType={tier.id === value ? "check" : "empty"}
                  label={formatToolResources(
                    {
                      resources: toolVersionResources,
                      credits: toolVersionCredits,
                    },
                    tier.id,
                  )}
                  onClick={() => {
                    if (tier.id !== parsedValue.value) {
                      onSelectResourceTier(tier.id);
                    }
                    stopEditing();
                  }}
                />
              )),
          ]}
        </EuiListGroup>
      </EuiPopover>
    );
  },
);
